import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, AlertTriangle } from 'lucide-react'
import { useLogin } from '../../auth/authProvider'
import { LeaseMDLogo } from '../ui/LeaseMDLogo'
import Button from '../ui/Button'
import ThemeToggle from '../ui/ThemeToggle'

interface Props {
  loginError?: string | null
}

export default function LoginPage({ loginError }: Props) {
  const login = useLogin()
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleLogin = async () => {
    setIsLoading(true)
    setLocalError(null)
    try {
      await login()
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? 'No se pudo iniciar sesión. Intenta de nuevo.'
      setLocalError(msg)
      setIsLoading(false)
    }
  }

  const errorMsg = localError ?? loginError

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-50 px-6 py-9 dark:bg-surface-950">
      <ThemeToggle className="absolute right-4 top-4 z-10" />
      <div className="w-full max-w-[400px]">
        <div className="text-center">
          <LeaseMDLogo variant="auto" className="mx-auto h-9 w-auto" />
          <p className="mt-3 text-sm text-surface-500 dark:text-surface-400">
            Recepción y validación de facturas de proveedores
          </p>
        </div>

        <div className="mt-6 rounded-[14px] border border-surface-200 bg-white p-8 shadow-sm dark:border-surface-800 dark:bg-surface-900">
          <h2 className="text-base font-semibold tracking-tight text-surface-900 dark:text-gray-100">
            Acceso para personal LeaseMD
          </h2>
          <p className="mt-1.5 text-sm text-surface-500 dark:text-surface-400">
            Entra con tu cuenta corporativa. Tus permisos vienen de Entra ID; no hay contraseña que capturar aquí.
          </p>

          <Button block size="lg" className="mt-5" onClick={handleLogin} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <svg viewBox="0 0 21 21" className="h-4 w-4 shrink-0" fill="currentColor">
                <rect x="1" y="1" width="9" height="9" />
                <rect x="11" y="1" width="9" height="9" />
                <rect x="1" y="11" width="9" height="9" />
                <rect x="11" y="11" width="9" height="9" />
              </svg>
            )}
            {isLoading ? 'Redirigiendo…' : 'Continuar con Microsoft'}
          </Button>

          {errorMsg && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-err-line bg-err-bg px-3 py-2 text-sm text-err-fg">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

        </div>

        <p className="mt-5 text-center text-sm text-surface-500 dark:text-surface-400">
          ¿Eres proveedor?{' '}
          <Link to="/p/login" className="text-brand-600 hover:underline dark:text-brand-400">
            Entra con tu RFC y correo
          </Link>
        </p>
      </div>
    </div>
  )
}
